import { useAppSelector } from "@APP/redux";
import { Amount, SchemeName } from "@APP/types";

export interface PaymentSourceData {
  isReusableRtp: boolean;
  /** Amount to display and send in the payment body. */
  amount: Amount;
  /** Displayed in the initiation screen confirmation text. */
  senderName: string;
  creditAccount: {
    schemeName: SchemeName;
    identification: string;
    name?: string;
  };
  remittanceReference?: string;
  /**
   * Returns the flat additionalData object to include in the payment body.
   * The caller passes the selected bankId which cannot be known before bank selection.
   */
  getAdditionalData: (bankId: string) => Record<string, string>;
  /**
   * The standard RTP request ID passed to API.initiateRTP.
   * Null for reusable RTP — that call must be skipped.
   */
  requestId: string | null;
}

/**
 * Normalises payment data from either the standard RTP slice or the
 * reusable RTP slice into a single consistent shape for use in
 * InitiateRTPPaymentScreen and RTPCompletePaymentScreen.
 *
 * Reusable RTP takes precedence when reusableRtpData is populated.
 * Returns null when neither slice has data (guarded against by route protectors).
 */
const usePaymentSource = (): PaymentSourceData | null => {
  const rtpState = useAppSelector((state) => state.rtp);
  const reusableRtpState = useAppSelector((state) => state.reusableRtp);

  if (reusableRtpState.reusableRtpData) {
    const { reusableRtpData, senderData, payerEnteredAmount, payerName, payerTipAmount } =
      reusableRtpState;
    const { paymentDetails } = reusableRtpData;

    const isVariable = parseFloat(paymentDetails.amount.amount) === 0;
    const baseAmount: Amount =
      isVariable && payerEnteredAmount
        ? { amount: payerEnteredAmount, currency: paymentDetails.amount.currency }
        : paymentDetails.amount;

    const amount: Amount =
      payerTipAmount && parseFloat(payerTipAmount) > 0
        ? {
            amount: (parseFloat(baseAmount.amount) + parseFloat(payerTipAmount)).toFixed(2),
            currency: baseAmount.currency,
          }
        : baseAmount;

    const getAdditionalData = (bankId: string): Record<string, string> => ({
      BankId: bankId,
      // reusableRtpId is a top-level field on the decode response (not inside additionalData).
      reusableRtpId: reusableRtpData.reusableRtpId,
      orgId: senderData?.companyInfo?.id ?? "",
      // erpId is not applicable for reusable RTP (no underlying ERP invoice).
      erpId: "",
      // payerName is included only when the payer actually entered a value.
      ...(payerName ? { payerName } : {}),
      ...(payerTipAmount && parseFloat(payerTipAmount) > 0 ? { tipAmount: payerTipAmount } : {}),
    });

    return {
      isReusableRtp: true,
      amount,
      senderName: senderData?.companyInfo?.name ?? senderData?.name ?? "",
      creditAccount: {
        schemeName: paymentDetails.creditAccount.schemeName,
        identification: paymentDetails.creditAccount.identification,
        name: paymentDetails.creditAccount.name,
      },
      remittanceReference: paymentDetails.remittanceInformation?.reference,
      getAdditionalData,
      requestId: null,
    };
  }

  if (rtpState.rtpData) {
    const { rtpData, senderData, rtp, payerTipAmount } = rtpState;

    const baseAmount = rtpData.paymentDetails.amount;
    const rtpAmount: Amount =
      payerTipAmount && parseFloat(payerTipAmount) > 0
        ? {
            amount: (parseFloat(baseAmount.amount) + parseFloat(payerTipAmount)).toFixed(2),
            currency: baseAmount.currency,
          }
        : baseAmount;

    return {
      isReusableRtp: false,
      amount: rtpAmount,
      senderName: senderData?.businessContact?.name ?? senderData?.name ?? "",
      creditAccount: {
        schemeName: rtpData.paymentDetails.creditAccount.schemeName,
        identification: rtpData.paymentDetails.creditAccount.identification,
        name: senderData?.businessContact?.name ?? senderData?.name,
      },
      remittanceReference: rtpData.payable?.reference,
      getAdditionalData: (bankId: string) => ({
        BankId: bankId,
        requestId: rtpData.paymentRequestId ?? "",
        erpId: rtpData.payable?.entityDetails?.source ?? "",
        invoiceId: rtpData.payable?.entityDetails?.externalId ?? "",
        invoiceType: rtpData.payable?.invoiceType ?? "",
        customerId: rtpData.payable?.supplierContact?.email ?? "",
        orgId: senderData?.companyInfo?.id ?? "",
        initialInvoiceType: rtpData.paymentDetails.additionalData?.initialInvoiceType ?? "",
        features: rtp?.receivable?.features?.join() ?? "",
        ...(payerTipAmount && parseFloat(payerTipAmount) > 0 ? { tipAmount: payerTipAmount } : {}),
      }),
      requestId: rtpData.paymentRequestId ?? null,
    };
  }

  return null;
};

export default usePaymentSource;
